import { useState, useEffect } from 'react'
import {
  Clock,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Check,
  X,
  Eye,
  Undo2,
  Info,
  Loader2
} from 'lucide-react'
import { pointsApi, exchangesApi } from '../../lib/api'
import { useAuth } from '../../lib/auth'

const MedicalVerify = () => {
  const [records, setRecords] = useState<any[]>([])
  const [confirmedRecords, setConfirmedRecords] = useState<any[]>([])
  const [selectedRecord, setSelectedRecord] = useState<any | null>(null)
  const [showModal, setShowModal] = useState(false)
  const [actionType, setActionType] = useState<'confirm' | 'cancel' | 'revoke'>('confirm')
  const [cancelReason, setCancelReason] = useState('')
  const [activeTab, setActiveTab] = useState<'pending' | 'confirmed'>('pending')
  const [loading, setLoading] = useState(true)

  /**
   * 角色判断走 auth 上下文。
   * ⚠️ 原先读 `localStorage.userAccount` —— 那是旧 mock 时代的键，医护登录写的是
   * `auth_user`（见 lib/auth.tsx），因此这里永远拿到 {}，`isSupervisor` 恒为 false，
   * 机构负责人会被当成普通医护（撤销按钮与审核提示全部消失）。2026-09-20 修正。
   */
  const { user } = useAuth()
  const isSupervisor = user?.role === 'medical' && user?.medicalRole === 'supervisor'

  useEffect(() => {
    loadRecords()
  }, [])

  const loadRecords = () => {
    setLoading(true)
    pointsApi.records({ page_size: 100 })
      .then((res: any) => {
        const items = res.records || []
        setRecords(items.filter((r: any) => r.status === 'pending'))
        setConfirmedRecords(items.filter((r: any) => r.status === 'confirmed'))
      })
      .catch(() => {
        setRecords([])
        setConfirmedRecords([])
      })
      .finally(() => setLoading(false))
  }

  // 检查是否在7天可撤销期限内
  const canRevoke = (record: any) => {
    if (record.status !== 'confirmed') return false
    const createdDate = new Date(record.created_at || record.createdAt || '')
    const now = new Date()
    const diffDays = Math.floor((now.getTime() - createdDate.getTime()) / (1000 * 60 * 60 * 24))
    return diffDays <= 7
  }

  // 获取剩余可撤销天数
  const getRemainingDays = (record: any) => {
    const createdDate = new Date(record.created_at || record.createdAt || '')
    const now = new Date()
    const diffDays = Math.floor((now.getTime() - createdDate.getTime()) / (1000 * 60 * 60 * 24))
    return 7 - diffDays
  }

  const handleOpenModal = (record: any, action: 'confirm' | 'cancel' | 'revoke') => {
    setSelectedRecord(record)
    setActionType(action)
    setCancelReason('')
    setShowModal(true)
  }

  const handleConfirm = () => {
    if (selectedRecord) {
      pointsApi.confirm(selectedRecord.id)
        .then(() => {
          const updated = { ...selectedRecord, status: 'confirmed' }
          setRecords(records.filter((r) => r.id !== selectedRecord.id))
          setConfirmedRecords([...confirmedRecords, updated])
          setShowModal(false)
          setSelectedRecord(null)
        })
        .catch(() => alert('确认失败'))
    }
  }

  const handleCancel = () => {
    if (selectedRecord && cancelReason) {
      pointsApi.cancel(selectedRecord.id)
        .then(() => {
          setRecords(records.filter((r) => r.id !== selectedRecord.id))
          setShowModal(false)
          setSelectedRecord(null)
        })
        .catch(() => alert('撤销失败'))
    }
  }

  const handleRevoke = () => {
    if (selectedRecord && cancelReason) {
      pointsApi.cancel(selectedRecord.id)
        .then(() => {
          setConfirmedRecords(confirmedRecords.filter((r) => r.id !== selectedRecord.id))
          setShowModal(false)
          setSelectedRecord(null)
          alert('撤销成功，积分已从用户账户扣除')
        })
        .catch(() => alert('撤销失败'))
    }
  }

  if (loading) {
    return (
      <div className="space-y-6 flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 text-green-600 animate-spin" />
        <span className="text-gray-500">加载中...</span>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* 页面标题 */}
      <div className="bg-white rounded-2xl shadow-md p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-gray-800 flex items-center">
              <Clock className="w-6 h-6 mr-2 text-orange-600" />
              事件审核管理
            </h1>
            <p className="text-gray-500 text-sm mt-1">
              {isSupervisor
                ? '审核、确认或撤销医护人员提交的事件'
                : '查看待审核的事件记录'}
            </p>
          </div>
        </div>
      </div>

      {/* Tab 切换 */}
      <div className="bg-white rounded-2xl shadow-md p-2 flex">
        <button
          onClick={() => setActiveTab('pending')}
          className={`flex-1 py-3 rounded-xl font-medium transition-colors flex items-center justify-center space-x-2 ${
            activeTab === 'pending'
              ? 'bg-green-700 text-white'
              : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          <Clock className="w-5 h-5" />
          <span>待审核 ({records.filter(r => r.status === 'pending').length})</span>
        </button>
        <button
          onClick={() => setActiveTab('confirmed')}
          className={`flex-1 py-3 rounded-xl font-medium transition-colors flex items-center justify-center space-x-2 ${
            activeTab === 'confirmed'
              ? 'bg-green-700 text-white'
              : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          <CheckCircle className="w-5 h-5" />
          <span>已确认 ({confirmedRecords.length})</span>
        </button>
      </div>

      {/* 7天撤销期限提示 */}
      {isSupervisor && confirmedRecords.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
          <div className="flex items-start space-x-3">
            <Info className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-medium text-blue-800">撤销期限说明</p>
              <p className="text-sm text-blue-600 mt-1">
                已确认的积分记录可在 <strong>7天内</strong> 进行撤销操作，超出期限后将无法撤销。
                请及时核对积分发放是否正确。
              </p>
            </div>
          </div>
        </div>
      )}

      {/* 待审核列表 */}
      {activeTab === 'pending' && (
        records.filter(r => r.status === 'pending').length === 0 ? (
          <div className="bg-white rounded-2xl shadow-md p-12 text-center">
            <div className="w-20 h-20 mx-auto bg-green-100 rounded-full flex items-center justify-center mb-4">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-800 mb-2">暂无待审核事件</h3>
            <p className="text-gray-500">所有事件都已处理完毕，干得漂亮!</p>
          </div>
        ) : (
          <div className="space-y-4">
            {records.filter(r => r.status === 'pending').map((record) => (
              <div
                key={record.id}
                className="bg-white rounded-2xl shadow-md p-6 border-l-4 border-orange-400"
              >
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                  <div className="flex items-start space-x-4">
                    <div className="w-14 h-14 bg-orange-100 rounded-xl flex items-center justify-center">
                      <Clock className="w-7 h-7 text-orange-600" />
                    </div>
                    <div>
                      <div className="flex items-center space-x-2 mb-1">
                        <h3 className="font-bold text-gray-800 text-lg">
                          {record.user_name || ''}
                        </h3>
                        <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">
                          {record.event_type_name || ''}
                        </span>
                      </div>
                      <div className="flex items-center space-x-4 text-sm text-gray-600">
                        <span>手机: {record.user_phone || ''}</span>
                        <span>操作员: {record.operator_name || record.operatorName || ''}</span>
                      </div>
                      <div className="flex items-center space-x-4 text-sm text-gray-500 mt-1">
                        <span>机构: {record.hospital_name || record.hospital || ''}</span>
                        <span>提交时间: {record.created_at || record.createdAt || ''}</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center space-x-3">
                    <div className="text-right mr-4">
                      <p className="text-3xl font-bold text-green-600">+{record.points}</p>
                      <p className="text-sm text-gray-500">积分</p>
                    </div>

                    {isSupervisor && (
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleOpenModal(record, 'confirm')}
                          className="flex items-center space-x-2 px-4 py-2 bg-green-700 text-white rounded-xl hover:bg-green-800 transition-colors"
                        >
                          <Check className="w-4 h-4" />
                          <span>确认</span>
                        </button>
                        <button
                          onClick={() => handleOpenModal(record, 'cancel')}
                          className="flex items-center space-x-2 px-4 py-2 border border-red-200 text-red-600 rounded-xl hover:bg-red-50 transition-colors"
                        >
                          <X className="w-4 h-4" />
                          <span>撤销</span>
                        </button>
                      </div>
                    )}

                    <button
                      onClick={() => setSelectedRecord(record)}
                      className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      <Eye className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )
      )}

      {/* 已确认列表 */}
      {activeTab === 'confirmed' && (
        confirmedRecords.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-md p-12 text-center">
            <div className="w-20 h-20 mx-auto bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <CheckCircle className="w-10 h-10 text-gray-400" />
            </div>
            <h3 className="text-xl font-bold text-gray-800 mb-2">暂无已确认记录</h3>
            <p className="text-gray-500">确认的事件将显示在这里</p>
          </div>
        ) : (
          <div className="space-y-4">
            {confirmedRecords.map((record) => (
              <div
                key={record.id}
                className="bg-white rounded-2xl shadow-md p-6 border-l-4 border-green-400"
              >
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                  <div className="flex items-start space-x-4">
                    <div className="w-14 h-14 bg-green-100 rounded-xl flex items-center justify-center">
                      <CheckCircle className="w-7 h-7 text-green-600" />
                    </div>
                    <div>
                      <div className="flex items-center space-x-2 mb-1">
                        <h3 className="font-bold text-gray-800 text-lg">
                          {record.user_name || ''}
                        </h3>
                        <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                          已确认
                        </span>
                        {canRevoke(record) && (
                          <span className="px-2 py-0.5 bg-orange-100 text-orange-700 text-xs font-medium rounded-full">
                            可撤销(剩{getRemainingDays(record)}天)
                          </span>
                        )}
                      </div>
                      <div className="flex items-center space-x-4 text-sm text-gray-600">
                        <span>手机: {record.user_phone || ''}</span>
                        <span>事件: {record.event_type_name || ''}</span>
                      </div>
                      <div className="flex items-center space-x-4 text-sm text-gray-500 mt-1">
                        <span>机构: {record.hospital_name || record.hospital || ''}</span>
                        <span>确认时间: {record.created_at || record.createdAt || ''}</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center space-x-3">
                    <div className="text-right mr-4">
                      <p className="text-3xl font-bold text-green-600">+{record.points}</p>
                      <p className="text-sm text-gray-500">积分</p>
                    </div>

                    {isSupervisor && canRevoke(record) && (
                      <button
                        onClick={() => handleOpenModal(record, 'revoke')}
                        className="flex items-center space-x-2 px-4 py-2 border border-red-200 text-red-600 rounded-xl hover:bg-red-50 transition-colors"
                      >
                        <Undo2 className="w-4 h-4" />
                        <span>撤销</span>
                      </button>
                    )}

                    {!canRevoke(record) && record.status === 'confirmed' && (
                      <span className="text-sm text-gray-400">已超过7天期限</span>
                    )}

                    <button
                      onClick={() => setSelectedRecord(record)}
                      className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      <Eye className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )
      )}

      

      {/* 操作模态框 */}
      {showModal && selectedRecord && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-2xl w-full max-w-md mx-4 shadow-2xl">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h3 className="text-lg font-bold text-gray-800">
                {actionType === 'confirm' ? '确认事件' : actionType === 'cancel' ? '撤销事件' : '撤销积分'}
              </h3>
              <button
                onClick={() => setShowModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              {actionType === 'confirm' ? (
                <>
                  <div className="bg-green-50 rounded-xl p-4 text-center">
                    <CheckCircle className="w-12 h-12 text-green-600 mx-auto mb-2" />
                    <p className="text-gray-700">
                      确认给用户 <span className="font-bold">{selectedRecord.user_name || ''}</span>{' '}
                      增加 <span className="font-bold text-green-600">+{selectedRecord.points}</span>{' '}
                      积分？
                    </p>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-4">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-gray-500">事件类型</p>
                        <p className="font-medium text-gray-800">{selectedRecord.event_type_name || ''}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">操作员</p>
                        <p className="font-medium text-gray-800">{selectedRecord.operator_name || selectedRecord.operatorName || ''}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">机构</p>
                        <p className="font-medium text-gray-800">{selectedRecord.hospital_name || selectedRecord.hospital || ''}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">提交时间</p>
                        <p className="font-medium text-gray-800">{selectedRecord.created_at || selectedRecord.createdAt || ''}</p>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className={`rounded-xl p-4 text-center ${actionType === 'revoke' ? 'bg-red-50' : 'bg-red-50'}`}>
                    <XCircle className="w-12 h-12 text-red-600 mx-auto mb-2" />
                    <p className="text-gray-700">
                      {actionType === 'revoke'
                        ? `确定要撤销给用户 ${selectedRecord.user_name || ''} 的积分吗？`
                        : '确定要撤销该积分事件吗？'
                      }
                    </p>
                    <p className="text-sm text-red-600 mt-2">
                      撤销后将从用户账户扣除 <strong>{selectedRecord.points}</strong> 积分
                    </p>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-4">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-gray-500">用户</p>
                        <p className="font-medium text-gray-800">{selectedRecord.user_name || ''}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">事件类型</p>
                        <p className="font-medium text-gray-800">{selectedRecord.event_type_name || ''}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">积分</p>
                        <p className="font-red-600">-{selectedRecord.points}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">时间</p>
                        <p className="font-medium text-gray-800">{selectedRecord.created_at || selectedRecord.createdAt || ''}</p>
                      </div>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      撤销原因 <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      rows={3}
                      placeholder="请输入撤销原因（必填）"
                      value={cancelReason}
                      onChange={(e) => setCancelReason(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500 resize-none"
                    />
                  </div>
                </>
              )}
            </div>

            <div className="flex items-center justify-end space-x-3 px-6 py-4 border-t">
              <button
                onClick={() => setShowModal(false)}
                className="px-6 py-2 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
              >
                取消
              </button>
              <button
                onClick={actionType === 'confirm' ? handleConfirm : actionType === 'cancel' ? handleCancel : handleRevoke}
                disabled={(actionType === 'cancel' || actionType === 'revoke') && !cancelReason}
                className={`px-6 py-2 rounded-xl text-white font-medium transition-colors ${
                  actionType === 'confirm'
                    ? 'bg-green-700 hover:bg-green-800'
                    : 'bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed'
                }`}
              >
                {actionType === 'confirm' ? '确认通过' : '确认撤销'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 详情模态框 */}
      {selectedRecord && !showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-2xl w-full max-w-lg mx-4 shadow-2xl">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h3 className="text-lg font-bold text-gray-800">事件详情</h3>
              <button
                onClick={() => setSelectedRecord(null)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="bg-gray-50 rounded-xl p-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">用户姓名</p>
                    <p className="font-medium text-gray-800">{selectedRecord.user_name || ''}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">手机号</p>
                    <p className="font-medium text-gray-800">{selectedRecord.user_phone || ''}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">事件类型</p>
                    <p className="font-medium text-gray-800">{selectedRecord.event_type_name || ''}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">获得积分</p>
                    <p className="font-bold text-green-600">+{selectedRecord.points}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">机构</p>
                    <p className="font-medium text-gray-800">{selectedRecord.hospital_name || selectedRecord.hospital || ''}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">操作员</p>
                    <p className="font-medium text-gray-800">{selectedRecord.operator_name || selectedRecord.operatorName || ''}</p>
                  </div>
                </div>
              </div>

              <div>
                <p className="text-sm text-gray-500 mb-1">状态</p>
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-orange-100 text-orange-700">
                  待确认
                </span>
              </div>

              <div>
                <p className="text-sm text-gray-500 mb-1">创建时间</p>
                <p className="font-medium text-gray-800">{selectedRecord.createdAt}</p>
              </div>
            </div>

            <div className="flex items-center justify-end px-6 py-4 border-t">
              <button
                onClick={() => setSelectedRecord(null)}
                className="px-6 py-2 bg-green-700 text-white rounded-xl hover:bg-green-800 transition-colors"
              >
                关闭
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default MedicalVerify
